import * as Sentry from '@sentry/react'
import { getAllPeople } from '../entities/people/service'

/**
 * Removes the current Sentry user identity and `user_extra` context.
 * Safe to call before any login (idempotent).
 */
export function clearSentryUserContext() {
  const scope = Sentry.getCurrentScope()
  scope.setUser(null)
  scope.setContext('user_extra', null)
}

/**
 * Tags Sentry with the user's identity and extra metadata.
 * If userInfo is null/undefined, clears instead (no stale context).
 * If email is missing, falls back to getAllPeople(client, [userInfo._id]).
 * Never throws — network or service errors degrade to breadcrumb + tag-without-email.
 */
export async function setSentryUserContext(userInfo, { client } = {}) {
  if (!userInfo) {
    clearSentryUserContext()
    return
  }

  const scope = Sentry.getCurrentScope()

  let email = userInfo.email || null
  if (!email && userInfo._id && client) {
    try {
      const people = await getAllPeople(client, { peopleIds: [userInfo._id] })
      email = people?.[0]?.email ?? null
    } catch (_err) {
      scope.addBreadcrumb({
        category: 'auth',
        message: 'getAllPeople failed while resolving email for sentry user context',
        level: 'error',
      })
    }
  }

  if (!email) {
    scope.addBreadcrumb({
      category: 'auth',
      message: 'email missing for sentry user context',
      level: 'warning',
    })
  }

  scope.setUser({
    id: userInfo._id,
    email: email || undefined,
    username: userInfo.fullName,
    name: userInfo.fullName,
  })

  scope.setContext('user_extra', {
    repliconId: userInfo.repliconId,
    position: userInfo.position,
    employeeType: userInfo.employeeType,
  })
}
